import {
  DEFAULT_DATE_RANGE,
  DEFAULT_STATUS,
} from '../../../CommunityFilters/types'
import type { BaseCommunityFilters } from '../../../CommunityFilters/types'
import { useChannelMentionCounts } from '../../../hooks/mentions/useChannelMentionCounts'
import { useHasChannelMentions } from '../../../hooks/mentions/useHasChannelMentions'
import {
  MentionsCaughtUpEmptyState,
  MentionsListeningEmptyState,
  NoMentionsFound,
} from '../../MentionsEmptyState'
import type { MentionsEmptyStateChannel } from '../../MentionsEmptyState'

import type { JSX } from 'react'

type UseMentionsEmptyStateProps = {
  /** The mention-supported channels in scope, already narrowed by the filter. */
  channels: MentionsEmptyStateChannel[]
  filters: BaseCommunityFilters
  /** Whether the sort/date/channel filters differ from their defaults. */
  hasActiveFilters: boolean
  onClearFilters: () => void
  /** Whether the list is currently rendering mention cards. */
  hasMentionsInList: boolean
  /** Whether every card in that list was resolved in this session. */
  allMentionsLocallyResolved: boolean
}

type UseMentionsEmptyStateReturn = {
  EmptyState: JSX.Element
  /** True while the answer is still being fetched; render the skeleton. */
  loading: boolean
  /**
   * Render the empty state *above* the still-visible list. Resolving a card
   * greys it in place rather than removing it, so the list never empties on
   * its own and the caught-up state has to sit over it.
   */
  showEmptyStateWithMentions: boolean
}

/**
 * Picks the empty state for the mentions list.
 *
 * The Replied and Resolved views list items the user has already dealt with,
 * so an empty one is about the filter and gets a filter-shaped message.
 * Everywhere else the split is whether these channels have ever had a mention:
 * Buffer imports no mention history on connect, so a channel that has never
 * had one needs that explained, while one that has needs nothing more than
 * "caught up".
 *
 * Two of those branches sit above a list that is still on screen — the All
 * view keeps showing answered mentions, and resolving a card greys it in place
 * rather than removing it.
 */
export const useMentionsEmptyState = ({
  channels,
  filters,
  hasActiveFilters,
  onClearFilters,
  hasMentionsInList,
  allMentionsLocallyResolved,
}: UseMentionsEmptyStateProps): UseMentionsEmptyStateReturn => {
  const { status, publishedAt } = filters

  // Unanswered and All are inbox views: emptying them is the goal, and the
  // caught-up state is the reward. Replied and Resolved are archives.
  const isArchiveView = status === 'replied' || status === 'dismissed'
  const isFilteredView =
    isArchiveView || (publishedAt != null && publishedAt !== DEFAULT_DATE_RANGE)

  // Cards the user just resolved are still on screen, which is its own proof
  // that the channel has mentions — no count needed.
  const justEmptiedTheList =
    hasMentionsInList && allMentionsLocallyResolved && !isArchiveView

  // A populated list already answers "has this channel ever had a mention".
  const { hasMentions, loading } = useHasChannelMentions({
    channels,
    skip: isFilteredView || hasMentionsInList,
  })

  // The All view lists answered mentions too, so its list never empties on its
  // own; nothing left unanswered is what being caught up means there.
  const { counts } = useChannelMentionCounts({
    channels,
    skip: isFilteredView || status !== DEFAULT_STATUS || !hasMentionsInList,
  })
  const noUnansweredLeft =
    !isFilteredView &&
    counts != null &&
    counts.every((channel) => channel.count === 0)

  if (justEmptiedTheList || (hasMentionsInList && noUnansweredLeft)) {
    return {
      EmptyState: (
        <MentionsCaughtUpEmptyState
          channels={channels}
          justResolvedLastMention={justEmptiedTheList}
          hasMentionsBelow
        />
      ),
      loading: false,
      showEmptyStateWithMentions: true,
    }
  }

  if (isFilteredView) {
    return {
      EmptyState: (
        <NoMentionsFound
          status={status}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={onClearFilters}
        />
      ),
      loading: false,
      showEmptyStateWithMentions: false,
    }
  }

  if (hasMentions === undefined && loading) {
    return {
      EmptyState: <MentionsCaughtUpEmptyState channels={channels} />,
      loading: true,
      showEmptyStateWithMentions: false,
    }
  }

  if (hasMentions === false) {
    return {
      EmptyState: <MentionsListeningEmptyState channels={channels} />,
      loading: false,
      showEmptyStateWithMentions: false,
    }
  }

  // `undefined` here means the count query failed. Fall back to the caught-up
  // state: it says nothing about the channel's history, whereas the listening
  // state would explain away missing mentions we have no evidence about.
  return {
    EmptyState: <MentionsCaughtUpEmptyState channels={channels} />,
    loading: false,
    showEmptyStateWithMentions: false,
  }
}
